import { useState, useEffect, createRef, useRef, useCallback } from 'react'
import Breadcrumb from './Breadcrumb'
import getImageUrl from './get-image-url.js'
import PageHeader from './PageHeader.jsx'
import '@mantine/core/styles.css'

import apiFetch from '@wordpress/api-fetch'

import { TextInput, Textarea, NativeSelect, Radio, Group, Checkbox } from '@mantine/core'
import { useForm } from '@mantine/form'

import { createTheme, MantineProvider } from '@mantine/core'

import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import BtnText from './BtnText'

import { useMediaQuery } from 'react-responsive'

import scrollIntoView from 'scroll-into-view-if-needed'

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

const scr = (node) => {
  scrollIntoView(node, {
    scrollMode: 'if-needed',
    block: 'start',
    inline: 'start',
  })
}

const theme = createTheme({
  /** Put your mantine theme override here */
  /** See full list of overridable components here: https://mantine.dev/theming/overrides */
  fontFamily: 'Noto Sans JP',
})

function Contact() {
  // userStateを宣言
  const [step, setStep] = useState(1)
  const [status, setStatus] = useState(null)
  const [options, setOptions] = useState([])
  const [token, setToken] = useState(null)

  const response_message = useRef('')

  const { executeRecaptcha } = useGoogleReCaptcha()

  // Create an event handler so you can call the verification on button click event or form submit
  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }

    const reCaptchaToken = await executeRecaptcha('contact')
    setToken(reCaptchaToken)
    // Do whatever you want with the token
  }, [executeRecaptcha])

  // You can use useEffect to trigger the verification as soon as the component being loaded
  useEffect(() => {
    handleReCaptchaVerify()
  }, [handleReCaptchaVerify])

  const isLg = useMediaQuery({ query: '(min-width: 1024px)' })

  // フォームのバリデーション
  // 氏名, ふりがな, メールアドレス, 電話番号, 住所, 希望職種, 運転免許の有無, 最終学歴, 保有資格, 現在の勤務状況, 希望年収, 希望入社日, 職歴, 主な職務経験, ご質問, 備考・連絡事項
  const schema = z.object({
    fullname: z.string().min(1, { message: 'この項目は必須です' }),
    // ふりがなのバリデーション
    furigana: z
      .string()
      .min(1, { message: 'この項目は必須です' })
      .regex(/^[ぁ-んー　]*$/, {
        message: 'ひらがなで入力してください',
      }),
    age: z
      .string()
      .regex(/^[0-9]{2}$/, {
        message: '半角数字で入力してください。２桁までのご年齢で入力してください。',
      })
      .min(1, { message: 'この項目は必須です' }),
    email: z
      .string()
      .email({ message: '有効なメールアドレスを入力してください。' })
      .or(z.string().optional()),
    phone_number: z
      .string()
      .regex(/^[0-9]{2,3}-?[0-9]{4}-?[0-9]{4}$/, {
        message: '有効な電話番号を入力してください。',
      })
      .min(1, { message: 'この項目は必須です' }),
    address: z.string(),
    job_category: z.string().min(1, { message: 'この項目は必須です' }),
    driving_license: z.string().min(1, { message: 'この項目は必須です' }),
    education: z.string().min(1, { message: 'この項目は必須です' }),
    qualification: z
      .array(z.string().min(1, { message: 'この項目は必須です' }))
      .min(1, { message: 'この項目は必須です' }),
    employment_status: z.string().min(1, { message: 'この項目は必須です' }),
    annual_income: z.string(),
    joining_date: z.string(),
    career: z.string(),
    job_experience: z.string(),
    question: z.string(),
    remarks: z.string(),
    confirm: z.literal(true, {
      errorMap: () => ({ message: 'チェックしてください' }),
    }),
  })

  // フォームの初期化
  // 氏名, ふりがな, メールアドレス, 電話番号, 住所, 希望職種, 運転免許の有無, 最終学歴, 保有資格, 現在の勤務状況, 希望年収, 希望入社日, 職歴, 主な職務経験, ご質問, 備考・連絡事項
  const form = useForm({
    initialValues: {
      fullname: '',
      furigana: '',
      age: '',
      email: '',
      phone_number: '',
      address: '',
      job_category: '',
      driving_license: '',
      education: '',
      qualification: [''],
      employment_status: '',
      annual_income: '',
      joining_date: '',
      career: '',
      job_experience: '',
      question: '',
      remarks: '',
    },
    validate: zodResolver(schema),
    validateInputOnBlur: true,
  })

  const qualification_fields = form.values.qualification.map((item, i) => {
    return (
      <div
        className="flex items-start gap-2"
        key={i}
      >
        <TextInput
          {...form.getInputProps(`qualification.${i}`)}
          className="grow"
        />
        {i === 0 ? (
          <div className="flex justify-center items-center w-[20px]">
            <button
              type="button"
              onClick={() => form.insertListItem('qualification', '')}
              className="text-[20px]"
            >
              +
            </button>
          </div>
        ) : (
          <div className="flex justify-center items-center w-[20px]">
            {form.values.qualification.length > 1 && (
              <button
                type="button"
                onClick={() => form.removeListItem('qualification', i)}
                className="text-[20px]"
              >
                -
              </button>
            )}
          </div>
        )}
      </div>
    )
  })

  const confirm = () => {
    form.validate()

    if (form.isValid()) {
      setStep(2)
    }

    scr(document.querySelector('.form-wrapper'))
  }

  const post = (raw) => {
    if (form.isValid()) {
      const data = new FormData()

      data.append('recaptcha_token', token)

      for (const key in raw) {
        if (key === 'qualification') {
          const val = raw[key]
          const str = val.join(', ')
          data.append(key, str)
        } else {
          data.append(key, raw[key])
        }
      }

      apiFetch({
        path: `${import.meta.env.VITE_SITE_URL}/wp-json/myroutes/application_form/`,
        method: 'POST',
        body: data,
      })
        .then((res) => {
          if (res.status === 'success') {
            setStatus('success')
            setStep(3)
            scr(document.querySelector('.content-area'))
          } else {
            setStatus('error')
            setStep(4)
            response_message.current = res.message
            scr(document.querySelector('.content-area'))
          }
        })
        .catch((err) => {
          setStatus('error')
          setStep(4)
          response_message.current = err.message
          scr(document.querySelector('.content-area'))
        })
    }
  }

  useEffect(() => {
    apiFetch({
      path: `${import.meta.env.VITE_SITE_URL}/wp-json/myroutes/application_form/`,
    }).then((res) => {
      let arr = res.map((item) => {
        return { label: item.crb_option, value: item.crb_option }
      })
      arr = [{ label: ' 選択してください', value: '' }, ...arr]
      setOptions(arr)
    })
  }, [])

  return (
    <>
      <PageHeader
        title={'応募フォーム'}
        image={getImageUrl('APPLICATION-FORM.png')}
      />
      <div className="content-area">
        <Breadcrumb
          current={'採用情報'}
          site_url={import.meta.env.VITE_SITE_URL}
          items={[]}
        />
      </div>
      <div>
        <div className="py-[min(calc(84vw/7.5),60px)] lg:pt-[114px] lg:pb-[60px]">
          <h1 className={status === 'success' ? ' lg:!text-[56px]' : null}>
            {status === 'success' ? 'ご応募ありがとうございます' : '基本情報'}
          </h1>
          {status === 'success' || (
            <p className="paragrah-1 mt-[20px]">
              <span className="text-[#cc4a1a]">*</span>は入力必須項目です。
            </p>
          )}
        </div>
        {(step === 1 || step === 2) && (
          <>
            <div className="form-wrapper relative">
              <div className="form-inner content-area">
                <MantineProvider theme={theme}>
                  <form onSubmit={form.onSubmit((values) => post(values))}>
                    {step === 1 && (
                      <>
                        <dl className="mt-[40px]">
                          <dt>
                            氏名<span>*</span>
                          </dt>
                          <dd>
                            <TextInput {...form.getInputProps('fullname')} />
                          </dd>

                          <dt>
                            ふりがな<span>*</span>
                          </dt>
                          <dd>
                            <TextInput {...form.getInputProps('furigana')} />
                          </dd>
                          <dt>
                            年齢<span>*</span>
                          </dt>
                          <dd>
                            <TextInput {...form.getInputProps('age')} />
                          </dd>
                          <dt>
                            電話番号<span>*</span>
                          </dt>
                          <dd>
                            <TextInput {...form.getInputProps('phone_number')} />
                          </dd>
                          <dt>メールアドレス</dt>
                          <dd>
                            <TextInput {...form.getInputProps('email')} />
                          </dd>
                          <dt>ご住所</dt>
                          <dd>
                            <TextInput {...form.getInputProps('address')} />
                          </dd>
                          <dt>
                            希望職種<span>*</span>
                          </dt>
                          <dd>
                            <NativeSelect
                              {...form.getInputProps('job_category')}
                              data={
                                options
                                  ? options
                                  : [
                                      {
                                        label: ' 選択してください',
                                        value: '',
                                      },
                                      { label: '選択肢1', value: '選択肢1' },
                                      { label: '選択肢2', value: '選択肢2' },
                                      { label: '選択肢3', value: '選択肢3' },
                                    ]
                              }
                            />
                          </dd>
                          <dt>
                            運転免許の有無<span>*</span>
                          </dt>
                          <dd>
                            <NativeSelect
                              {...form.getInputProps('driving_license')}
                              data={[
                                { label: ' 選択してください', value: '' },
                                { label: '有り', value: '有り' },
                                { label: '無し', value: '無し' },
                              ]}
                            />
                          </dd>
                          <dt>
                            最終学歴<span>*</span>
                          </dt>
                          <dd>
                            <NativeSelect
                              {...form.getInputProps('education')}
                              data={[
                                { label: ' 選択してください', value: '' },
                                { label: '大卒', value: '大卒' },
                                { label: '高卒', value: '高卒' },
                                { label: '中卒', value: '中卒' },
                              ]}
                            />
                          </dd>
                          <dt>
                            保有資格<span>*</span>
                          </dt>
                          <dd>
                            <div className="grid gap-3">{qualification_fields}</div>
                          </dd>
                          <dt>
                            現在の勤務状況<span>*</span>
                          </dt>
                          <dd>
                            <Radio.Group {...form.getInputProps('employment_status')}>
                              <Group>
                                <Radio
                                  value="就業中"
                                  label="就業中"
                                />
                                <Radio
                                  value="即就業可"
                                  label="即就業可"
                                />
                                <Radio
                                  value="卒業見込"
                                  label="卒業見込"
                                />
                                <Radio
                                  value="その他"
                                  label="その他"
                                />
                              </Group>
                            </Radio.Group>
                          </dd>
                          <dt>希望年収</dt>
                          <dd>
                            <TextInput {...form.getInputProps('annual_income')} />
                          </dd>
                          <dt>希望入社日</dt>
                          <dd>
                            <TextInput {...form.getInputProps('joining_date')} />
                          </dd>
                          <dt>職歴</dt>
                          <dd>
                            <Textarea {...form.getInputProps('career')} />
                          </dd>
                          <dt>主な職務経験</dt>
                          <dd>
                            <Textarea {...form.getInputProps('job_experience')} />
                          </dd>
                          <dt>ご質問</dt>
                          <dd>
                            <Textarea {...form.getInputProps('question')} />
                          </dd>
                          <dt>備考・連絡事項</dt>
                          <dd>
                            <Textarea {...form.getInputProps('remarks')} />
                          </dd>
                        </dl>
                        <Checkbox
                          {...form.getInputProps('confirm')}
                          value="はい"
                          label={'個人情報の取り扱いについて、下記事項に同意します。'}
                          className="flex justify-center mt-9"
                        />
                        <BtnText
                          className="m-[68px_auto_0]"
                          onClick={() => confirm()}
                          arrow={false}
                        >
                          確認する
                        </BtnText>
                      </>
                    )}
                    {step === 2 && (
                      <>
                        <dl className="mt-[40px]">
                          <dt>
                            氏名<span>*</span>
                          </dt>
                          <dd>{form.values.fullname}</dd>

                          <dt>
                            ふりがな<span>*</span>
                          </dt>
                          <dd>{form.values.furigana}</dd>
                          <dt>
                            年齢<span>*</span>
                          </dt>
                          <dd>{form.values.age}</dd>
                          <dt>
                            電話番号<span>*</span>
                          </dt>
                          <dd>{form.values.phone_number}</dd>
                          <dt>メールアドレス</dt>
                          <dd>{form.values.email}</dd>
                          <dt>ご住所</dt>
                          <dd>{form.values.address}</dd>
                          <dt>
                            希望職種<span>*</span>
                          </dt>
                          <dd>{form.values.job_category}</dd>
                          <dt>運転免許の有無</dt>
                          <dd>{form.values.driving_license}</dd>
                          <dt>最終学歴</dt>
                          <dd>{form.values.education}</dd>
                          <dt>保有資格</dt>
                          <dd>{form.values.qualification.join(', ')}</dd>
                          <dt>現在の勤務状況</dt>
                          <dd>{form.values.employment_status}</dd>
                          <dt>希望年収</dt>
                          <dd>{form.values.annual_income}</dd>
                          <dt>希望入社日</dt>
                          <dd>{form.values.joining_date}</dd>
                          <dt>職歴</dt>
                          <dd>{form.values.career}</dd>
                          <dt>主な職務経験</dt>
                          <dd>{form.values.job_experience}</dd>
                          <dt>ご質問</dt>
                          <dd>{form.values.question}</dd>
                          <dt>備考・連絡事項</dt>
                          <dd>{form.values.remarks}</dd>
                        </dl>
                        <div className="flex flex-wrap justify-center gap-y-3 gap-x-4 mt-9">
                          <BtnText
                            white={true}
                            onClick={() => setStep(1)}
                            className="border-2 !border-[#003475]"
                            arrow={false}
                          >
                            戻って編集する
                          </BtnText>
                          <BtnText
                            className="btn-1"
                            type="submit"
                            onClick={() => scr(document.querySelector('.content-area'))}
                          >
                            送信する
                          </BtnText>
                        </div>
                      </>
                    )}
                  </form>
                </MantineProvider>
              </div>
            </div>
            <div className="form-policy">
              <div className="inner">
                <h3>プライバシーポリシー</h3>
                <p className="mt-[20px]">
                  横浜総合建設株式会社（以下、当社）では、お客様のプライバシーを尊重し、お客様の個人情報を
                  大切に保護することを重要な責務と考えております。当ウェブサイトでは、個人情報保護に関する
                  法令を遵守するとともに、個人情報の取扱に関して次のような姿勢で行動しています。
                </p>
                <h4 className="mt-[40px]">個人情報の利用目的</h4>
                <p className="mt-[10px]">
                  当社では、ユーザーのみなさまから受け取った個人情報（現行システムではメールアドレス）は、
                  ユーザーのみなさまに当社からご連絡させていただく場合にのみ利用します。
                </p>
                <h4 className="mt-[40px]">第三者への提供</h4>
                <p className="mt-[10px]">
                  お客様からのご提供頂いた個人情報は、お客様のご了承がない限り収集した個人情報を第三者に
                  提供致しません。当社以外の企業／団体から皆様に有益と思われる情報のお届けを代行する場合にも、お客様のご承諾がない限り、個人情報はそうした企業／団体には開示・提供は致しません。
                  ただし、以下の場合は除きます。
                  <br />
                  ・利用目的に必要な限りにおいて、当社の業務委託先に対し開示を行う場合。
                </p>
              </div>
            </div>
          </>
        )}
        {step === 3 && (
          <div className="px-[min(calc(70vw/7.5),35px)]">
            <p className="paragrah-3">
              確認のために、自動返信メールをお送りしております。
              <br />
              万が一、自動返信メールが届かない場合は、
              {isLg ? <br /> : null}
              お手数ですがお電話にてお問い合わせください。
            </p>
            <div className="flex justify-center mt-[60px] lg:mt-[100px]">
              <BtnText
                href={import.meta.env.VITE_SITE_URL}
                arrow={false}
              >
                トップページへ
              </BtnText>
            </div>
          </div>
        )}
        {step === 4 && (
          <>
            <h3
              style={{
                textAlign: 'center',
                font: 'normal normal bold 30px/44px Noto Sans JP',
                color: 'red',
                marginBottom: '20px',
              }}
            >
              送信エラー
            </h3>
            <p
              style={{
                textAlign: 'center',
                fontSize: '16px',
                marginBottom: '20px',
                fontWeight: 'bold',
              }}
            >
              {response_message.current}
            </p>
            <p className="paragrah-3">
              申し訳ございません、送信に失敗しました。
              <br />
              セキュリティーソフトやアプリをオフにして頂き再度お試しいただくか、
              {isLg ? <br /> : ''}
              お手数ですがお電話にてお問い合わせください。
            </p>
            <div className="flex justify-center mt-[60px] lg:mt-[100px]">
              <BtnText
                href={import.meta.env.VITE_SITE_URL}
                arrow={false}
              >
                トップページへ
              </BtnText>
            </div>
          </>
        )}
      </div>
      <style jsx>{`
        h1 {
          text-align: center;
          font: normal normal bold calc(50vw / 7.5) Noto Sans JP;
          color: #2964b6;
        }
        .paragrah-1 {
          text-align: center;
          font: normal normal 500 calc(30vw / 7.5) Noto Sans JP;
        }
        .paragrah-3 {
          font: normal normal normal calc(35vw / 7.5) Noto Sans JP;
        }
        .form-inner {
          background: #ffffff 0% 0% no-repeat padding-box;
          padding-bottom: calc(214vw / 7.5);
        }
        .btn-1 {
          background: #003475 0% 0% no-repeat padding-box;
          border-radius: 6px;
          font: normal normal bold 20px/27px Noto Sans JP;
          color: #ffffff;
          padding: 20px 120px;
          margin: 68px auto 0;
          display: block;
          border: 2px solid transparent;
        }
        .form-link {
          text-decoration: underline;
          font: normal normal 500 20px/29px Noto Sans JP;
        }
        .form-policy {
          background: #f7f7f7 0% 0% no-repeat padding-box;
          padding: calc(86vw / 7.5) calc(70vw / 7.5);
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .form-policy .inner {
          max-width: 704px;
        }
        .form-policy h3 {
          font: normal normal bold 30px/44px Noto Sans JP;
          text-align: center;
        }
        .form-policy h4 {
          font: normal normal bold 20px/29px Noto Sans JP;
        }
        .form-policy p {
          font: normal normal normal 16px/24px Noto Sans JP;
        }
        dt:last-of-type,
        dd:last-of-type {
          border-bottom: none;
        }
        dt {
          width: 240px;
          font: normal normal normal 20px Noto Sans JP;
        }
        dt span {
          color: #cc4a1a;
        }
        dd {
          padding: calc(15vw / 7.5) 0 calc(22vw / 7.5);
        }
        @media screen and (min-width: 768px) {
          h1 {
            text-align: center;
            font: normal normal bold 80px/80px Noto Sans JP;
            color: #2964b6;
          }
          .paragrah-1 {
            text-align: center;
            font: normal normal 500 20px/58px Noto Sans JP;
          }
          .paragrah-3 {
            text-align: center;
            font: normal normal normal 25px/50px Noto Sans JP;
          }
          .form-wrapper {
            background-image: linear-gradient(180deg, #2964b6 0%, #2760b3 100%),
              url(${getImageUrl('form-bg.png')});
            background-size: cover, 1920px 699px;
            background-repeat: no-repeat;
            background-position: bottom;
            background-blend-mode: multiply;
            padding: 106px 0;
          }
          .form-inner {
            background: #ffffff 0% 0% no-repeat padding-box;
            border-radius: 40px;
            padding: 80px 150px;
          }
          .btn-1 {
            background: #003475 0% 0% no-repeat padding-box;
            border-radius: 6px;
            font: normal normal bold 20px/27px Noto Sans JP;
            color: #ffffff;
            padding: 20px 120px;
            margin: 68px auto 0;
            display: block;
            border: 2px solid transparent;
          }
          .btn-2 {
            background: #fff 0% 0% no-repeat padding-box;
            border-radius: 6px;
            font: normal normal bold 20px/27px Noto Sans JP;
            color: #003475;
            padding: 20px 120px;
            margin: 68px auto 0;
            display: block;
            border: 2px solid #003475;
          }
          .form-link {
            text-decoration: underline;
            font: normal normal 500 20px/29px Noto Sans JP;
          }
          .form-policy {
            background: #f7f7f7 0% 0% no-repeat padding-box;
            padding: 100px 0;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .form-policy .inner {
            max-width: 704px;
          }
          .form-policy h3 {
            font: normal normal bold 30px/44px Noto Sans JP;
            text-align: center;
          }
          .form-policy h4 {
            font: normal normal bold 20px/29px Noto Sans JP;
          }
          .form-policy p {
            font: normal normal normal 16px/24px Noto Sans JP;
          }
          dl {
            display: grid;
            grid-template-columns: auto 1fr;
          }
          dt,
          dd {
            border-bottom: 1px solid #b4b4b4;
            padding: 15px 0;
          }
          dt:last-of-type,
          dd:last-of-type {
            border-bottom: none;
          }
          dt {
            width: 240px;
            font: normal normal normal 20px Noto Sans JP;
          }
          dt span {
            color: #cc4a1a;
          }
        }
      `}</style>
    </>
  )
}

export default Contact
